import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, TrendingUp, CheckCircle2, Clock, Plus, RefreshCw, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const risk = data?.risk;
  const score = risk?.overall ?? 0;
  const scoreClass = score >= 80 ? "success" : score >= 60 ? "warning" : "danger";
  const scoreLabel = score >= 80 ? "Healthy" : score >= 60 ? "At Risk" : "Critical";

  return (
    <AppLayout>
      <div className="p-8 lg:p-10 max-w-[1400px]">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">Risk Dashboard</div>
            <h1 className="font-serif text-4xl tracking-tight font-medium" data-testid="dashboard-title">Compliance overview</h1>
          </div>
          <Link to="/cases/new" className="fa-btn-primary" data-testid="dashboard-new-case">
            <Plus className="w-4 h-4" /> New case
          </Link>
        </div>

        {!data && <div className="text-slate-500 text-sm">Loading…</div>}

        {data && (
          <>
            {/* Score */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
              <div className="lg:col-span-4 fa-card p-8">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Agency Risk Score</div>
                <div className="flex items-baseline gap-3 mb-2">
                  <div className="font-serif text-7xl font-medium" data-testid="risk-score-value">{score}</div>
                  <div className="text-slate-500 text-lg">/ 100</div>
                </div>
                <div className={`fa-badge fa-badge-${scoreClass === "success" ? "success" : scoreClass === "warning" ? "warning" : "danger"}`} data-testid="risk-score-label">{scoreLabel}</div>
                <div className="risk-bar mt-6">
                  <div className={`risk-bar-fill ${scoreClass}`} style={{ width: `${score}%` }} />
                </div>
                <div className="text-xs text-slate-500 mt-3">Calculated from deadline compliance, backlog, audit completeness, redaction accuracy, and staffing.</div>
              </div>

              <div className="lg:col-span-8 fa-card p-8">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Score breakdown</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5" data-testid="risk-breakdown">
                  {Object.entries(risk.breakdown).map(([k, v]) => (
                    <div key={k}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="text-sm capitalize">{k.replace(/_/g, " ")}</div>
                        <div className="font-mono text-sm">{v}</div>
                      </div>
                      <div className="risk-bar">
                        <div className={`risk-bar-fill ${v >= 80 ? "success" : v >= 60 ? "warning" : "danger"}`} style={{ width: `${v}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-slate-200 bg-white mb-8">
              <Metric icon={Clock} label="Total cases" value={risk.metrics.total_cases} testid="metric-total" />
              <Metric icon={AlertTriangle} label="Open" value={risk.metrics.open_cases} testid="metric-open" />
              <Metric icon={AlertTriangle} label="Overdue" value={risk.metrics.overdue_cases} testid="metric-overdue" tone="danger" />
              <Metric icon={CheckCircle2} label="Closed" value={risk.metrics.closed_cases} testid="metric-closed" />
              <Metric icon={TrendingUp} label="Avg response (days)" value={risk.metrics.avg_response_days} testid="metric-avg" />
            </div>

            {/* Captured Risk Assessment (from public funnel) */}
            {data.captured_assessment && data.captured_assessment.risk_score != null && (
              <div className="fa-card p-6 mb-8 border-l-4 border-l-[#C5A059]" data-testid="captured-assessment-card">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                  <div className="md:col-span-1">
                    <ShieldAlert className="w-7 h-7 text-[#C5A059]" strokeWidth={1.5} />
                  </div>
                  <div className="md:col-span-7">
                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">From your initial assessment</div>
                    <div className="font-serif text-lg leading-snug">
                      You scored{" "}
                      <span className="font-semibold" data-testid="captured-assessment-score">
                        {data.captured_assessment.risk_score} / 100
                      </span>{" "}
                      {data.captured_assessment.risk_grade && (
                        <span className="text-slate-500 text-sm">({data.captured_assessment.risk_grade})</span>
                      )}{" "}
                      when you joined.
                    </div>
                    <div className="text-sm text-slate-600 mt-1">
                      Once you&apos;ve processed a few cases, re-run the assessment to measure how your defensibility has improved.
                    </div>
                  </div>
                  <div className="md:col-span-4 md:text-right">
                    <Link to="/risk-assessment" className="fa-btn-secondary" data-testid="rerun-assessment-link">
                      <RefreshCw className="w-4 h-4" /> Re-run assessment
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Recent cases */}
            <div className="fa-card">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <div className="text-xs uppercase tracking-widest text-slate-500">Recent cases</div>
                <Link to="/cases" className="text-sm text-[#0B1B3D] underline decoration-[#C5A059] underline-offset-2" data-testid="view-all-cases-link">View all →</Link>
              </div>
              {data.recent_cases.length === 0 ? (
                <div className="p-10 text-center text-slate-500">No cases yet. Click &quot;New case&quot; to create your first request.</div>
              ) : (
                <table className="fa-table">
                  <thead>
                    <tr>
                      <th>Case #</th>
                      <th>Requester</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th>Deadline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_cases.map((c) => (
                      <CaseRow key={c.case_id} c={c} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

function Metric({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="p-6 border-r border-slate-200 last:border-r-0">
      <div className="flex items-center gap-2 mb-3 text-slate-500">
        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
        <div className="text-xs uppercase tracking-widest">{label}</div>
      </div>
      <div className={`font-serif text-3xl ${tone === "danger" && value > 0 ? "text-[#DC2626]" : ""}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function CaseRow({ c }) {
  const daysLeft = Math.ceil((new Date(c.deadline) - new Date()) / 86400000);
  const overdue = daysLeft < 0;
  const atRisk = daysLeft >= 0 && daysLeft <= 3;
  return (
    <tr>
      <td>
        <Link to={`/cases/${c.case_id}`} className="font-mono text-xs hover:underline" data-testid={`case-row-${c.case_id}`}>
          {c.case_number}
        </Link>
      </td>
      <td>{c.requester_name}</td>
      <td className="max-w-md truncate">{c.subject}</td>
      <td><StatusBadge status={c.status} /></td>
      <td>
        {overdue ? (
          <span className="fa-badge fa-badge-danger">{Math.abs(daysLeft)}d overdue</span>
        ) : atRisk ? (
          <span className="fa-badge fa-badge-warning">{daysLeft}d left</span>
        ) : (
          <span className="text-slate-500 text-sm">{daysLeft}d left</span>
        )}
      </td>
    </tr>
  );
}

export function StatusBadge({ status }) {
  const map = {
    new: ["info", "New"],
    assigned: ["info", "Assigned"],
    document_collection: ["neutral", "Documents"],
    redaction: ["warning", "Redaction"],
    legal_review: ["warning", "Legal review"],
    approved: ["success", "Approved"],
    responded: ["success", "Responded"],
  };
  const [tone, label] = map[status] || ["neutral", status];
  return <span className={`fa-badge fa-badge-${tone}`}>{label}</span>;
}
